"use client";
import { useEffect, useState } from "react";
import { Modal } from "@/utils/bootstrap";
import { usePathname } from "next/navigation";
import { useAtom } from "jotai";
import { useAccount } from "@/hooks/useAccount";
import { isSignedInAtom } from "@/hooks/authAtoms";
import CustomButton, { BtnRadius, BtnSizes, BtnTypes } from "./CustomButton";
import {
  VerifiyTypes,
  useNeedAccountVerify,
} from "@/hooks/useNeedAccountVerify";
import { accountConfirmQuery } from "@/app/components/pages-components/auth-pages/register/register-confirm/AccountConfirm";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useRouter } from "./navigation/useRouter";
import { useIsMobile } from "@/hooks/useIsMobile";
import { LoginMethodTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useRemoveLangFromPath } from "@/hooks/useRemoveLangFromPath";
const ConfirmAccountModal = () => {
  const isMobileSize = useIsMobile();
  const router = useRouter();
  const pathname = usePathname();
  const account = useAccount();
  const userProfile = useGetProfileQuery();
  const { email, mobile } = userProfile?.data?.result ?? {};
  const [signedIn] = useAtom(isSignedInAtom);
  const [show, setShow] = useState(false);
  const t = useTranslation();
  const needConfirm = useNeedAccountVerify();
  const removeLangFromPath = useRemoveLangFromPath();
  const currPath = removeLangFromPath(pathname, true);
  const hide =
    !signedIn ||
    !needConfirm ||
    [
      PagesPaths.profile,
      PagesPaths.register,
      PagesPaths.register_confirm,
    ]?.includes(currPath! as PagesPaths);
  useEffect(() => {
    setShow(!hide);
    if (!hide) router.push("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hide]);
  if (!show) return null;

  const mobileNotConfirmed = needConfirm === VerifiyTypes.mobile;
  const emailNotConfirmed = needConfirm === VerifiyTypes.email;
  const mobileRequired = mobileNotConfirmed && !mobile;
  const emailRequired = emailNotConfirmed && !email;
  const hideConfirmBtn = mobileRequired || emailRequired;
  const handleVerify = () => {
    setShow(false);
    router.push(
      accountConfirmQuery({
        confirm: mobileNotConfirmed
          ? LoginMethodTypes.by_phone
          : LoginMethodTypes.by_email,
        redirectTo: pathname!,
      }),
    );
  };

  const handleLogout = () => {
    setShow(false);
    account.logout();
  };

  const handleMyProfile = () => {
    setShow(false);
    router.push(PagesPaths.profile);
  };
  const msgFirstLine = mobileRequired
    ? t.mobile_is_required
    : emailRequired
    ? t.email_is_required
    : mobileNotConfirmed
    ? t.should_verify_mobile
    : t.should_verify_email;
  const msgSecondLine = hideConfirmBtn
    ? t.please_go_to_profile_and_add_it
    : mobileNotConfirmed
    ? mobile
    : email;
  return (
    <Modal
      centered
      show={show}
      onHide={() => setShow(false)}
      backdrop="static"
      dialogClassName="messagebox-dialog"
      contentClassName="edit-note-dialog-content w-100"
    >
      <div className="cart-Item-dialog-title">{t.account_verify}</div>
      <div className="h3-text-center">
        {msgFirstLine}
        <div className="mb-2">{msgSecondLine}</div>
      </div>
      <div
        className={`d-flex justify-content-between ${
          isMobileSize ? "flex-wrap" : ""
        }`}
        style={{ gap: 16 }}
      >
        <CustomButton
          type={BtnTypes.secondary}
          size={BtnSizes.medium}
          radius={BtnRadius.small}
          onClick={handleLogout}
          fullWidth
        >
          {t.logout}
        </CustomButton>
        <CustomButton
          type={hideConfirmBtn ? BtnTypes.primary : BtnTypes.secondary}
          size={BtnSizes.medium}
          radius={BtnRadius.small}
          onClick={handleMyProfile}
          fullWidth
        >
          {t.my_profile}
        </CustomButton>
        {!hideConfirmBtn && (
          <CustomButton
            type={BtnTypes.primary}
            size={BtnSizes.medium}
            radius={BtnRadius.small}
            fullWidth
            onClick={() => {
              setShow(false);
              handleVerify();
            }}
          >
            {mobileNotConfirmed ? t.verify_mobile : t.verify_email}
          </CustomButton>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmAccountModal;
